import {
  type ExternalIntegrationNames,
  type IntegrationItemNames,
  ReviewPlatformLabels,
  ReviewPlatformNames,
  SyncIntegrationNames,
  ZapierTemplateNames,
} from 'common/constants/autopilotIntegrations';

import AppStoreLogo from 'img/app-store-logo-small.svg';
import CapterraLogo from 'img/capterra-logo-small.svg';
import FreshdeskLogo from 'img/freshdesk-logo-small.svg';
import G2Logo from 'img/g2-logo-small.svg';
import GongLogo from 'img/gong-logo.png';
import GoogleFormsLogo from 'img/google-forms-logo-small.png';
import GooglePlayLogo from 'img/google-play-logo-small.svg';
import HelpscoutLogo from 'img/helpscout-logo.png';
import IntercomLogo from 'img/landing/intercom-logo.svg';
import ZendeskLogo from 'img/landing/zendesk-logo.svg';
import ProductHuntLogo from 'img/product-hunt-logo-small.svg';
import SalesforceLogo from 'img/salesforce-text-logo-small.svg';
import ShopifyLogo from 'img/shopify-logo-small.svg';
import SurveyMonkeyLogo from 'img/survey-monkey-logo-small.svg';
import TLDVLogo from 'img/tldv-logo-small.svg';
import TrustpilotLogo from 'img/trustpilot-logo-small.svg';
import TrustRadiusLogo from 'img/trustradius-logo-small.svg';
import TypeformLogo from 'img/typeform-logo-small.png';
import WordpressLogo from 'img/wordpress-logo-small.svg';
import ZapierLogo from 'img/zapier-logo-square-medium.png';
import ZoomLogo from 'img/zoom-logo-small.png';

import type { LucideIcon } from 'lucide-react';

export type SourceFilter = {
  description: string;
  component: React.ComponentType;
};

export enum SourceType {
  integration = 'integration',
  review = 'review',
  zapier = 'zapier',
}

type BaseIntegration = {
  label: string;
  name: SyncIntegrationNames;
  icon: string;
  sourceFilters?: true;
};

type ReviewIntegration = {
  type: SourceType.review;
  platform: ReviewPlatformNames;
  needsCountry?: boolean;
  credits: number;
};

type ConversationIntegration = {
  type: SourceType.integration;
  url: string;
  platform?: never;
  needsCountry?: never;
  credits: number;
};

type ZapierIntegration = {
  type: SourceType.zapier;
  credits: number | { min: number; max: number };
  url: string;
  platform?: never;
  needsCountry?: never;
};

export type Integration = BaseIntegration &
  (ReviewIntegration | ConversationIntegration | ZapierIntegration);

type ConfigurablePoint = {
  type: 'configurable';
  beforeText: string;
  afterText: string;
  placeholder: string;
};

type StaticPoint = {
  type: 'static';
  text: string;
};

type InfoPoint = ConfigurablePoint | StaticPoint;

type InfoPoints = {
  [key in SyncIntegrationNames]?: InfoPoint[];
} & {
  conversationDefault: InfoPoint[];
  reviewDefault: InfoPoint[];
};

const ticketDefaultInfoPoints: InfoPoint[] = [
  { type: 'static', text: 'Pulls feedback from tickets closed in the past 30 days' },
  { type: 'static', text: 'Continuously collects feedback from newly closed tickets' },
  { type: 'static', text: 'Uses your current credit balance—no extra charges unless specified' },
];

export const InfoPoints: InfoPoints = {
  conversationDefault: [
    { type: 'static', text: 'Pulls feedback from conversations closed in the past 30 days' },
    { type: 'static', text: 'Continuously collects feedback from newly closed conversations' },
    { type: 'static', text: 'Uses your current credit balance—no extra charges unless specified' },
  ],
  reviewDefault: [
    { type: 'static', text: 'Pulls feedback from reviews left in the last 100 days' },
    { type: 'static', text: 'Continuously gathers new feedback from new reviews' },
    { type: 'static', text: 'Uses 1 credit per review, up until your allotted credit limit' },
  ],
  [SyncIntegrationNames.freshdesk]: ticketDefaultInfoPoints,
  [SyncIntegrationNames.zendesk]: ticketDefaultInfoPoints,
  [SyncIntegrationNames.intercom]: [
    {
      type: 'configurable',
      beforeText: 'Pulls feedback from conversations closed in the past ',
      afterText: ' days (180 days max)',
      placeholder: '30',
    },
    { type: 'static', text: 'Continuously collects feedback from newly closed conversations' },
    { type: 'static', text: 'Uses your current credit balance—no extra charges unless specified' },
  ],
};

export const ConfigurableDayLimitIntegrations: SyncIntegrationNames[] = [
  SyncIntegrationNames.intercom,
  // Add more integrations here as they support configurable day limits
];

export type IntegrationItem = {
  credits: number;
  label: string;
  logo: string | LucideIcon;
  name: ExternalIntegrationNames;
  needsCountry?: boolean;
  platform?: ReviewPlatformNames;
  type: IntegrationItemNames;
};

export type ZapierIntegrationItem = Omit<IntegrationItem, 'name' | 'credits' | 'type'> & {
  credits: number | { max: number; min: number };
  logo: string;
  name: ZapierTemplateNames;
  url: string;
};

export const ZapierIntegrations: ZapierIntegrationItem[] = [
  {
    credits: 1,
    label: 'Google Forms',
    logo: GoogleFormsLogo,
    name: ZapierTemplateNames.googleForms,
    url: 'https://zapier.com/apps/canny/integrations/google-forms/255563355/scan-new-form-responses-in-google-forms-with-autopilot-in-canny-and-create-or-update-canny-users',
  },
  {
    credits: 1,
    label: 'Survey Monkey',
    logo: SurveyMonkeyLogo,
    name: ZapierTemplateNames.surveyMonkey,
    url: 'https://zapier.com/apps/canny/integrations/surveymonkey/255563364/create-users-and-scan-new-survey-monkey-responses-with-canny',
  },
  {
    credits: 1,
    label: 'Typeform',
    logo: TypeformLogo,
    name: ZapierTemplateNames.typeform,
    url: 'https://zapier.com/apps/canny/integrations/typeform/255563371/scan-with-autopilot-and-createupdate-users-in-canny-from-new-typeform-entries',
  },
];

export const ZapierGenericIntegration: ZapierIntegrationItem = {
  credits: { min: 1, max: 5 },
  label: 'Build your own source',
  logo: ZapierLogo,
  name: ZapierTemplateNames.generic,
  url: 'https://zapier.com/apps/canny/integrations',
};

const conversationSources = [
  {
    label: 'Intercom',
    name: SyncIntegrationNames.intercom,
    icon: IntercomLogo,
    sourceFilters: true,
    url: '/admin/settings/intercom',
    credits: 1,
  },
  {
    label: 'Freshdesk',
    name: SyncIntegrationNames.freshdesk,
    icon: FreshdeskLogo,
    url: '/admin/settings/freshdesk',
    credits: 1,
  },
  {
    label: 'Zendesk',
    name: SyncIntegrationNames.zendesk,
    icon: ZendeskLogo,
    sourceFilters: true,
    url: '/admin/settings/zendesk',
    credits: 1,
  },
  {
    label: 'Help Scout',
    name: SyncIntegrationNames.helpscout,
    icon: HelpscoutLogo,
    url: '/admin/settings/helpscout',
    credits: 1,
  },
  {
    label: 'Gong',
    name: SyncIntegrationNames.gong,
    icon: GongLogo,
    url: '/admin/settings/gong',
    credits: 5,
  },
  {
    label: 'tl;dv',
    name: SyncIntegrationNames.tldv,
    icon: TLDVLogo,
    url: '/admin/settings/tldv',
    credits: 5,
  },
  {
    label: 'Zapier',
    name: SyncIntegrationNames.zapier,
    icon: ZapierLogo,
    url: 'https://zapier.com/apps/canny/integrations',
    credits: 1,
  },
  {
    label: 'Zoom',
    name: SyncIntegrationNames.zoom,
    icon: ZoomLogo,
    url: '/admin/settings/zoom',
    credits: 5,
  },
];

// TODO: bring into conversationSources above once getting-started can handle Zapier
export const ZapierSources: Integration[] = ZapierIntegrations.map((zapTemplate) => ({
  label: zapTemplate.label,
  icon: zapTemplate.logo,
  type: SourceType.zapier,
  name: SyncIntegrationNames.zapier,
  url: zapTemplate.url,
  credits: zapTemplate.credits,
}));

export const mappedConversationSources: Record<string, Integration> = conversationSources.reduce(
  (map, { label, name, icon, url, credits }) => ({
    ...map,
    [name]: {
      label,
      name,
      icon,
      type: SourceType.integration,
      url,
      credits,
    },
  }),
  {},
);

const reviewSources = [
  {
    name: SyncIntegrationNames.salesforceReviews,
    label: ReviewPlatformLabels.salesforce,
    icon: SalesforceLogo,
    platform: ReviewPlatformNames.salesforce,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.appStoreReviews,
    icon: AppStoreLogo,
    platform: ReviewPlatformNames.appStore,
    needsCountry: true,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.playStoreReviews,
    icon: GooglePlayLogo,
    platform: ReviewPlatformNames.playStore,
    needsCountry: true,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.g2Reviews,
    icon: G2Logo,
    platform: ReviewPlatformNames.g2,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.capterraReviews,
    icon: CapterraLogo,
    platform: ReviewPlatformNames.capterra,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.trustpilotReviews,
    icon: TrustpilotLogo,
    platform: ReviewPlatformNames.trustpilot,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.trustradiusReviews,
    icon: TrustRadiusLogo,
    platform: ReviewPlatformNames.trustradius,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.productHuntReviews,
    icon: ProductHuntLogo,
    platform: ReviewPlatformNames.productHunt,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.wordpressReviews,
    icon: WordpressLogo,
    platform: ReviewPlatformNames.wordpress,
    credits: 1,
  },
  {
    name: SyncIntegrationNames.shopifyReviews,
    icon: ShopifyLogo,
    platform: ReviewPlatformNames.shopify,
    credits: 1,
  },
];

const mappedReviewSources = reviewSources.reduce(
  (map, { name, icon, platform, needsCountry, label, credits }) => ({
    ...map,
    [name]: {
      label: label ?? platform,
      name,
      icon,
      platform,
      needsCountry,
      type: SourceType.review,
      credits,
    },
  }),
  {},
);

export const IntegrationMap: Record<string, Integration> = {
  ...mappedConversationSources,
  ...mappedReviewSources,
};

export const Integrations = Object.values(IntegrationMap);
